import { BackgroundCheckColumns } from '@/backend/features/background-check/shared';
import { BackgroundCheckType } from '@/backend/features/background-check/types';
import { requireManageMembersAndAnySystemPermission } from '@/backend/utils';
import { BackgroundCheckTypeSelectModal } from '@/ui/features/background-check/components/background-check-modal/BackgroundCheckTypeSelectModal';
import { SlideOutMenuItemWithId } from '@/ui/hooks';
import { showCustomDialog, showErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { AccountColumns, AccountData, SystemStatus, hasLowerSystemRole } from '@juvare/contact-management/backend/features/accounts/shared';
import {
    collectOrgIdsForAnyOrgPermission,
    requireAnySystemPermission,
} from '@juvare/contact-management/backend/features/permissions/shared';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { GetResponse, Row } from '@juvare/platform-shared/features/storage';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { BackgroundCheckModal } from '../components/background-check-modal';

interface IsAllowedToRequestBackgroundCheckParams {
    isBackgroundCheckTypeEnabled: boolean;
    targetAccount: {
        id: string;
        systemStatus: SystemStatus;
        orgMembership: string[] | undefined;
    };
}

export function useRequestBackgroundCheckDetails(): {
    handleRequestBackgroundCheck: (
        account: AccountData,
        backgroundCheck: BackgroundCheckColumns,
        accountId: string,
        reloadAccounts?: () => void,
        onClose?: () => void,
    ) => void;
    handlePreRequestBackgroundCheck: (
        account: AccountData,
        backgroundChecks: Row<BackgroundCheckColumns>[],
        accountId: string,
        reloadAccounts?: () => void,
    ) => void;
    isAllowedToRequestBackgroundCheck: (params: IsAllowedToRequestBackgroundCheckParams) => boolean;
    getBackgroundCheckMenuItems: (
        { data, id }: GetResponse<AccountData, AccountColumns>,
        backgroundChecks?: Row<BackgroundCheckColumns>[],
        reloadAccounts?: () => void,
    ) => SlideOutMenuItemWithId[];
} {
    const { backgroundCheckConfig, systemRoleMap } = useSelector(getSettings);
    const { t } = useTranslation();
    const userState = useUserState();
    const navigate = useNavigate();
    const userStateId = userState.id;

    const canRequestBackgroundCheck = requireManageMembersAndAnySystemPermission(userState, 'request-background-check');

    const handleReviewProfile = React.useCallback(
        (accountId: string) => {
            navigate(`/accounts/${accountId}?tab=backgroundcheck`);
        },
        [navigate],
    );

    const isAllowedToRequestBackgroundCheck = React.useCallback(
        ({
            isBackgroundCheckTypeEnabled,
            targetAccount: { id: accountId, systemStatus, orgMembership = [] },
        }: IsAllowedToRequestBackgroundCheckParams) => {
            const isOwnAccount = userStateId === accountId;
            const isAccountActive = systemStatus === 'active';

            const manageableOrganizationIds = collectOrgIdsForAnyOrgPermission(userState, 'manage-members');
            const canManageAccountByOrgPermission = orgMembership.some((orgId) => manageableOrganizationIds.includes(orgId));
            const canManageAccount = requireAnySystemPermission(userState, 'manage-accounts') || canManageAccountByOrgPermission;

            return canRequestBackgroundCheck && canManageAccount && isBackgroundCheckTypeEnabled && isAccountActive && !isOwnAccount;
        },
        [canRequestBackgroundCheck, userState, userStateId],
    );

    const handleRequestBackgroundCheck = React.useCallback(
        (
            accountData: AccountData,
            backgroundCheck: BackgroundCheckColumns,
            accountId: string,
            reloadAccounts?: () => void,
            onClose?: () => void,
        ) => {
            const isConsentGiven = Boolean(backgroundCheck.consent);
            const backgroundCheckConfigByType = backgroundCheckConfig.type[backgroundCheck.type as BackgroundCheckType];

            const validConsent = backgroundCheckConfigByType.requiresConsent ? isConsentGiven : true;

            if (!validConsent) {
                return showErrorDialog({ title: t('backgroundCheck.messages.userDidNotConsent') });
            }

            if (hasLowerSystemRole(userState.systemRole, accountData.systemRole, systemRoleMap)) {
                return showErrorDialog({ title: t('backgroundCheck.messages.cannotPerformBackgroundCheck') });
            }

            showCustomDialog({
                content: (close, ariaTitleId, ariaContentId) => (
                    <BackgroundCheckModal
                        account={accountData}
                        backgroundCheck={backgroundCheck}
                        accountId={accountId}
                        ariaContentId={ariaContentId}
                        ariaTitleId={ariaTitleId}
                        onCloseModal={() => {
                            close();

                            if (onClose) {
                                onClose();
                            }
                        }}
                        onReloadAccounts={reloadAccounts}
                    />
                ),
            });
        },
        [backgroundCheckConfig.type, systemRoleMap, t, userState.systemRole],
    );

    const handlePreRequestBackgroundCheck = React.useCallback(
        (account: AccountData, backgroundChecks: Row<BackgroundCheckColumns>[], accountId: string, reloadAccounts?: () => void) => {
            showCustomDialog({
                content: (close, ariaTitleId, ariaContentId) => (
                    <BackgroundCheckTypeSelectModal
                        account={account}
                        backgroundCheckData={backgroundChecks}
                        accountId={accountId}
                        ariaContentId={ariaContentId}
                        ariaTitleId={ariaTitleId}
                        onCloseModal={close}
                        handleRequestBackgroundCheck={handleRequestBackgroundCheck}
                        onReloadAccounts={reloadAccounts}
                    />
                ),
            });
        },
        [handleReviewProfile, systemRoleMap, t, userState.systemRole],
    );

    const getBackgroundCheckMenuItems = React.useCallback(
        (
            { data, columns, id }: GetResponse<AccountData, AccountColumns>,
            backgroundChecks?: Row<BackgroundCheckColumns>[],
            reloadAccounts?: () => void,
        ): SlideOutMenuItemWithId[] => {
            const items: SlideOutMenuItemWithId[] = [];

            if (!backgroundChecks) {
                return items;
            }

            if (
                isAllowedToRequestBackgroundCheck({
                    targetAccount: {
                        id,
                        systemStatus: data.systemStatus,
                        orgMembership: columns.orgMembership,
                    },
                    isBackgroundCheckTypeEnabled: !!backgroundChecks?.length,
                })
            ) {
                if (backgroundChecks.length === 1) {
                    items.push({
                        id: 'menu-item-background-check',
                        label: t('menuItems.backgroundCheck'),
                        onClick: () => handleRequestBackgroundCheck(data, backgroundChecks[0].columns, id, reloadAccounts),
                    });
                } else {
                    items.push({
                        id: 'menu-item-background-check',
                        label: t('menuItems.backgroundCheck'),
                        onClick: () => handlePreRequestBackgroundCheck(data, backgroundChecks, id, reloadAccounts),
                    });
                }
            }

            return items;
        },
        [isAllowedToRequestBackgroundCheck, handleRequestBackgroundCheck, handlePreRequestBackgroundCheck, t],
    );

    return {
        getBackgroundCheckMenuItems,
        isAllowedToRequestBackgroundCheck,
        handleRequestBackgroundCheck,
        handlePreRequestBackgroundCheck,
    };
}
